import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { format } from 'date-fns';
import { getReports, ReportDTO, downloadFromBucket } from '../../hooks/scripts';
import { useError } from '@src/contexts/ErrorContext';
import { colors } from '@src/styles';

interface ReportsListModalProps {
  open: boolean;
  onClose: () => void;
  scriptId: string;
  autoRefresh?: boolean;
}

const STATUS_CONFIG: Record<ReportDTO['status'], { label: string; color: string }> = {
  PENDING: { label: 'Pendente', color: '#FFA726' },
  SUCCESS: { label: 'Concluído', color: '#66BB6A' },
  ERROR: { label: 'Falhou', color: '#EF5350' },
};

export const ReportsListModal = ({ open, onClose, scriptId, autoRefresh = false }: ReportsListModalProps) => {
  const [reports, setReports] = useState<ReportDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { setError, setSuccess } = useError();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchReports = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    try {
      const reportsList = await getReports(scriptId);
      setReports(reportsList);
    } catch (error: any) {
      console.error('Error fetching reports:', error);
      if (isManualRefresh) {
        setError({
          title: 'Erro ao atualizar relatórios',
          message: error?.response?.data?.message || error?.message || 'Não foi possível atualizar os relatórios',
          statusCode: error?.response?.status,
        });
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchReports();

      // Long polling if autoRefresh is enabled
      if (autoRefresh) {
        pollingIntervalRef.current = setInterval(() => {
          fetchReports();
        }, 5000); // Poll every 5 seconds
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [open, scriptId, autoRefresh]);

  const handleDownload = async (report: ReportDTO) => {
    try {
      const filename = `report-${report.id}-${format(new Date(report.createdAt), 'yyyyMMdd-HHmmss')}.xlsx`;
      await downloadFromBucket(report.path, filename);

      setSuccess({
        title: 'Download concluído',
        message: 'O relatório foi baixado com sucesso',
      });
    } catch (error: any) {
      console.error('Error downloading report:', error);
      setError({
        title: 'Erro ao baixar relatório',
        message: error?.response?.data?.message || error?.message || 'Não foi possível baixar o relatório',
        statusCode: error?.response?.status,
      });
    }
  };

  const handleClose = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1e1e1e',
          backgroundImage: 'none',
          maxHeight: '700px'
        },
      }}
    >
      <DialogTitle sx={{ bgcolor: '#1e1e1e', borderBottom: '1px solid #333' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Extrações de Relatórios
          </Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Atualizar">
              <IconButton
                onClick={() => fetchReports(true)}
                size="small"
                disabled={isRefreshing}
                sx={{ color: '#fff' }}
              >
                {isRefreshing ? <CircularProgress size={20} sx={{ color: '#2196f3' }} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleClose} size="small" sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#1e1e1e', pt: 3 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress sx={{ color: '#2196f3' }} />
          </Box>
        ) : reports.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography sx={{ color: '#aaa' }}>Nenhuma extração encontrada</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#aaa', borderBottom: '1px solid #333' }}>Data de Criação</TableCell>
                  <TableCell sx={{ color: '#aaa', borderBottom: '1px solid #333' }}>Status</TableCell>
                  <TableCell align="center" sx={{ color: '#aaa', borderBottom: '1px solid #333' }}>
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id} sx={{ '&:hover': { bgcolor: '#252525' } }}>
                    <TableCell sx={{ color: '#fff', borderBottom: '1px solid #333' }}>
                      {format(new Date(report.createdAt), 'dd/MM/yyyy HH:mm:ss')}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #333' }}>
                      <Chip
                        label={STATUS_CONFIG[report.status].label}
                        size="small"
                        sx={{
                          bgcolor: `${STATUS_CONFIG[report.status].color}20`,
                          color: STATUS_CONFIG[report.status].color,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ borderBottom: '1px solid #333' }}>
                      <Tooltip title="Baixar">
                        <IconButton
                          onClick={() => handleDownload(report)}
                          disabled={report.status !== 'SUCCESS'}
                          sx={{
                            color: report.status === 'SUCCESS' ? '#2196f3' : '#666',
                            '&:hover': {
                              bgcolor: report.status === 'SUCCESS' ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                            },
                          }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ bgcolor: '#1e1e1e', borderTop: '1px solid #333', px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          sx={{
            color: '#aaa',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
